import { PlatformIcon } from '@/components/social/PlatformIcon';
import { Check, Link2, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ShareBarProps {
    title: string;
    url?: string;
    image?: string | null;
    label?: string;
    className?: string;
}

type SocialPlatform = 'pinterest' | 'linkedin' | 'facebook' | 'x';

export function ShareBar({ title, url, image, label = "Know someone who'd love this?", className = '' }: ShareBarProps) {
    const [shareUrl, setShareUrl] = useState(url ?? '');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!url && typeof window !== 'undefined') {
            setShareUrl(window.location.href);
        }
    }, [url]);

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(title);
    const encodedImage = image ? encodeURIComponent(image) : '';

    const socialLinks: { platform: SocialPlatform; label: string; href: string }[] = [
        {
            platform: 'pinterest',
            label: 'Share on Pinterest',
            href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedTitle}`,
        },
        {
            platform: 'linkedin',
            label: 'Share on LinkedIn',
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        },
        {
            platform: 'facebook',
            label: 'Share on Facebook',
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        },
        {
            platform: 'x',
            label: 'Share on X',
            href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        },
    ];

    const mailHref = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;

    const handleCopy = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            const input = document.createElement('input');
            input.value = shareUrl;
            document.body.appendChild(input);
            input.select();
            try {
                document.execCommand('copy');
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
            } finally {
                document.body.removeChild(input);
            }
        }
    };

    const iconLinkClass =
        'group inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#0e2727]/15 text-[#0e2727]/55 transition-colors duration-200 hover:border-[#0e2727]/50 hover:text-[#0e2727] focus-visible:border-[#0e2727]/50 focus-visible:text-[#0e2727] focus-visible:outline-none';

    return (
        <div className={`mx-auto max-w-3xl px-6 ${className}`}>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
                <span
                    className="text-[15px] leading-none italic"
                    style={{
                        fontFamily: "'Didot', 'Playfair Display', Georgia, serif",
                        color: 'rgba(14, 39, 39, 0.6)',
                    }}
                >
                    {label}
                </span>
                <span aria-hidden="true" className="hidden h-5 w-px sm:inline-block" style={{ backgroundColor: 'rgba(14, 39, 39, 0.18)' }} />
                <div className="flex items-center gap-2.5">
                    {socialLinks.map(({ platform, label: linkLabel, href }) => (
                        <a key={platform} href={href} target="_blank" rel="noopener noreferrer" aria-label={linkLabel} className={iconLinkClass}>
                            <PlatformIcon platform={platform} size="sm" className="h-[13px] w-[13px] text-current" />
                        </a>
                    ))}
                    <a href={mailHref} aria-label="Share by email" className={iconLinkClass}>
                        <Mail size={14} strokeWidth={1.5} />
                    </a>
                    <button type="button" onClick={handleCopy} aria-label={copied ? 'Link copied' : 'Copy link'} className={iconLinkClass}>
                        {copied ? <Check size={14} strokeWidth={1.75} /> : <Link2 size={14} strokeWidth={1.5} />}
                    </button>
                </div>
                <span className="sr-only" aria-live="polite">
                    {copied ? 'Link copied to clipboard' : ''}
                </span>
            </div>
        </div>
    );
}
